import _ from 'lodash';
import { switchExpiryDate } from '../../../utilities/card-utilities';

export const getRemainingRecipients = (cartItem) => {
  const totalRecipientQuantity = _.sumBy(cartItem.recipients, (recipient) => recipient.quantity);
  return cartItem.quantity - totalRecipientQuantity;
};

export const getCreateOrderRequest = (
  cashOrderCartItems,
  cashOrderDefaults,
  deliveryOption,
  singleShippingAddress,
  physicalAddressRecipient,
  shippingMethod,
  isFeeWaiveOverride,
  promoDiscount,
  requestedName,
  requestedPhone,
  caseNumber,
  purchaseOrderNumber
) => {
  const cardsToCreate = getCardsToCreate(
    cashOrderCartItems,
    cashOrderDefaults,
    deliveryOption,
    singleShippingAddress,
    physicalAddressRecipient
  );

  return {
    dispatchMethod: shippingMethod,
    promotionCodeDiscountOverride: promoDiscount,
    requestorName: requestedName,
    requestorPhone: requestedPhone,
    requestorCase: caseNumber,
    purchaseOrderNumber: purchaseOrderNumber,
    isFeeWaiveOverride: isFeeWaiveOverride,
    cardsToCreate: cardsToCreate,
  };
};

const getCardsToCreate = (
  cashOrderCartItems,
  cashOrderDefaults,
  deliveryOption,
  singleShippingAddress,
  physicalAddressRecipient
) => {
  const cardsToCreate = [];
  function generateCard(cartItem, recipient) {
    const isPhysical = cartItem.mediaType === 'P';

    return {
      denominationCode: cartItem?.denominationCode,
      denomination: cartItem?.denomination,
      quantity:
        !isPhysical && deliveryOption === 'optionThree' && recipient?.quantity
          ? recipient?.quantity
          : cartItem?.quantity,
      isFuelOnly: cartItem?.isFuelOnly,
      deliveryAddress: getDeliveryAddress(
        deliveryOption,
        cashOrderDefaults,
        singleShippingAddress,
        isPhysical,
        recipient
      ),
      expiryDate: switchExpiryDate(cartItem?.expiryDate),
      mediaType: cartItem?.mediaType,
      verificationType: cartItem?.verificationType,
      plasticType: cartItem?.plasticTypeCode,
    };
  }

  Object.entries(cashOrderCartItems)?.forEach(([, cartItem]) => {
    if (cartItem?.recipients && cartItem?.recipients.length) {
      Object.entries(cartItem?.recipients)?.forEach(([, recipient]) => {
        cardsToCreate.push(generateCard(cartItem, recipient));
      });
    } else {
      cardsToCreate.push(generateCard(cartItem, physicalAddressRecipient));
    }
  });

  return cardsToCreate;
};

const getDeliveryAddress = (deliveryOption, cashOrderDefaults, singleShippingAddress, isPhysical, recipientAddress) => {
  if (deliveryOption === 'optionOne') {
    return {
      firstName: cashOrderDefaults?.address?.firstName,
      lastName: cashOrderDefaults?.address?.lastName,
      country: 'AUS',
      state: cashOrderDefaults?.address?.stateCode,
      city: cashOrderDefaults?.address?.city,
      postCode: cashOrderDefaults?.address?.postCode,
      line1: cashOrderDefaults?.address?.line1,
      line2: cashOrderDefaults?.address?.line2,
      line3: cashOrderDefaults?.address?.line3,
      email: cashOrderDefaults?.address?.email,
      freeText: cashOrderDefaults?.address?.addInfo,
      mobilePhone: cashOrderDefaults?.address?.mobilePhone,
    };
  } else if (deliveryOption === 'optionTwo') {
    return {
      firstName: singleShippingAddress?.firstName,
      lastName: singleShippingAddress?.lastName,
      country: 'AUS',
      state: singleShippingAddress?.state,
      city: singleShippingAddress?.suburb,
      postCode: singleShippingAddress?.postcode,
      line2: singleShippingAddress?.address,
      email: singleShippingAddress?.email,
      freeText: singleShippingAddress?.message,
      mobilePhone: singleShippingAddress?.mobile,
    };
  } else if (deliveryOption === 'optionThree') {
    if (isPhysical) {
      return {
        firstName: recipientAddress?.firstName,
        lastName: recipientAddress?.lastName,
        country: 'AUS',
        state: recipientAddress?.state,
        city: recipientAddress?.suburb,
        postCode: recipientAddress?.postcode,
        line2: recipientAddress?.address,
        email: recipientAddress?.email,
        mobilePhone: recipientAddress?.mobile,
      };
    } else {
      return {
        firstName: recipientAddress?.firstName,
        lastName: recipientAddress?.lastName,
        email: recipientAddress?.email,
        freeText: recipientAddress?.message,
      };
    }
  }
};

export const isDigitalCartOnly = (cashOrderCartItems) => {
  return Object.values(cashOrderCartItems).every((item) => item.mediaType !== 'P');
};

const EXTRA_OPTIONS = {
  fuel: { label: 'Fuel only', value: 'fuel' },
  pin: { label: 'PIN required', value: 'pin' },
};

export const resolveAllowedExtras = ({ mediaType, denomination, fleetLimitters = [] }) => {
  const isWetStock = fleetLimitters.includes('Wet Stock');
  const isPhysical = mediaType === 'P';
  const isPinRequired = Number(denomination) >= 1000;

  if (isWetStock) {
    if (!isPhysical) {
      return {
        allowedExtras: [EXTRA_OPTIONS.fuel],
        defaultExtras: ['fuel'],
      };
    }

    if (isPinRequired) {
      return {
        allowedExtras: [EXTRA_OPTIONS.fuel, EXTRA_OPTIONS.pin],
        defaultExtras: ['fuel', 'pin'],
      };
    }

    return {
      allowedExtras: [EXTRA_OPTIONS.fuel],
      defaultExtras: ['fuel'],
    };
  }

  const allowedExtras = [EXTRA_OPTIONS.fuel, EXTRA_OPTIONS.pin];

  if (isPhysical && isPinRequired) {
    return {
      allowedExtras,
      defaultExtras: ['pin'],
    };
  }

  return {
    allowedExtras,
    defaultExtras: [],
  };
};

export const getExtrasList = (isDigital, cardDenomination, fleetLimitters = []) => {
  return resolveAllowedExtras({
    mediaType: isDigital ? 'D' : 'P',
    denomination: cardDenomination,
    fleetLimitters,
  }).allowedExtras;
};
